import type { Signal } from '@preact/signals-react';
import { useState } from 'react';
import ColumnVisibility from './column-visibility.component';
import type { DataGridColumnSettings } from './data-grid-settings.interface';

export interface DataGridSettingsMenuItem {
  label?: string;
  key: string;
  isSeparator?: boolean;
  subMenuItems?: Array<SubMenuItem>;
}

export interface SubMenuItem {
  label?: string;
  key: string;
  isSeparator?: boolean;
}

export interface DataGridSettingsSubMenuProps {
  items: Array<DataGridSettingsMenuItem>;
  onItemClick: (menuKey: string) => void;
  subMenuVisibility: boolean;
}

const SubmenuItems = ({ items, onItemClick, subMenuVisibility }: DataGridSettingsSubMenuProps) => {
  return (
    <div
      style={{ left: '-50px' }}
      className={`inset-y-15 absolute -top-2 z-10 ${
        !subMenuVisibility && 'hidden'
      } origin-top-left rounded-bl-md rounded-tl-md bg-mono-1 py-2 text-sm shadow-md drop-shadow`}
    >
      {items.map((item) => {
        return (
          <div
            key={item.key}
            className={`cursor-pointer leading-6 ${item.isSeparator ? 'py-1' : 'px-3 py-px hover:bg-primary-4'}`}
            onClick={() => onItemClick(item.key)}
          >
            {!item.isSeparator ? item.label : <hr className="border-gray-3" />}
          </div>
        );
      })}
    </div>
  );
};

export interface DataGridSettingsMenuProps {
  items: Array<DataGridSettingsMenuItem>;
  onItemClick: (menuKey: string) => void;
  columns: Signal<Array<DataGridColumnSettings>>;
  onColumnsVisibilityChanged: (columns: Array<DataGridColumnSettings>) => void;
  showGridColumnsOnSettingsMenu: boolean;
  setOverlayVisible?: (value: boolean) => void;
  showGridMenu?: boolean;
}

export default function DataGridSettingsMenu({
  items,
  onItemClick,
  columns,
  onColumnsVisibilityChanged,
  showGridColumnsOnSettingsMenu,
  setOverlayVisible,
  showGridMenu = true,
}: DataGridSettingsMenuProps) {
  const [subMenuVisibility, setSubMenuVisibility] = useState(false);
  return (
    <div className="bg-mono-1 text-sm">
      {showGridMenu &&
        items.map((item) => (
          <div
            key={item.key}
            className={`hover:bg-gray-5 relative cursor-pointer rounded-xs px-3 py-1 leading-6 ${
              item.isSeparator ? 'py-1' : 'px-3 py-px hover:bg-primary-4'
            }`}
            onClick={() => onItemClick(item.key)}
            onMouseEnter={() => item?.subMenuItems && setSubMenuVisibility(true)}
            onMouseLeave={() => item?.subMenuItems && setSubMenuVisibility(false)}
          >
            {!item.isSeparator ? item.label : <hr className="border-gray-3" />}
            {item?.subMenuItems && (
              <SubmenuItems
                items={item?.subMenuItems}
                onItemClick={onItemClick}
                subMenuVisibility={subMenuVisibility}
              />
            )}
          </div>
        ))}
      {showGridColumnsOnSettingsMenu && (
        <ColumnVisibility
          columns={columns}
          onColumnsVisibilityChanged={onColumnsVisibilityChanged}
          setOverlayVisible={setOverlayVisible}
        />
      )}
    </div>
  );
}
